/**
 * API client for eos-app (v2 API with organization support)
 *
 * Reason: Separate client for organization-scoped APIs that use Auth0 session cookies
 * instead of explicit token headers
 */

import axios, { AxiosInstance } from 'axios';

interface AuthTokens {
  authToken: string;
  idToken: string;
}

class ApiV2 {
  private static authTokens: AuthTokens;
  private static orgId: string | null = null;
  private static impersonateEmail: string | null = null;
  private static axiosInstance: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_EOS_API_2,
    withCredentials: true, // Important: Send Auth0 session cookies
  });

  /**
   * Sets the organization ID for all subsequent requests
   *
   * @reason Backend requires orgId in x-org-id header for org-scoped operations
   */
  public static setOrgId(orgId: string): void {
    this.orgId = orgId;
  }

  /**
   * Sets the email of the user to impersonate (super_admin only)
   *
   * @reason Allows super_admin to view the system as another user for troubleshooting
   */
  public static setImpersonateEmail(email: string | null): void {
    this.impersonateEmail = email;
  }

  /**
   * Gets the current impersonation email
   */
  public static getImpersonateEmail(): string | null {
    return this.impersonateEmail;
  }

  /**
   * Sets authentication tokens for API requests
   *
   * @reason Maintains compatibility with existing auth flow while adding session support
   */
  public static setAuthResult(authTokens: AuthTokens): void {
    this.authTokens = authTokens;

    // Add auth headers to requests
    this.axiosInstance.interceptors.request.use((config) => {
      if (config.headers) {
        config.headers['Authorization'] = `Bearer ${this.authTokens.authToken}`;
        config.headers['Identity'] = `Token ${this.authTokens.idToken}`;

        // Add orgId header if available
        if (this.orgId) {
          config.headers['x-org-id'] = this.orgId;
        }

        // Add impersonation header if super_admin is impersonating
        if (this.impersonateEmail) {
          config.headers['x-impersonate-email'] = this.impersonateEmail;
        }
      }
      return config;
    });
  }

  public static async get<T>(url: string, params?: Record<string, string>): Promise<T> {
    const response = await this.axiosInstance.get(url, { params });
    return response.data as T;
  }

  public static async post<TResponse, TBody = any>(url: string, body?: TBody): Promise<TResponse> {
    const response = await this.axiosInstance.post(url, body);
    return response.data as TResponse;
  }

  public static async patch<TResponse, TBody = any>(url: string, body?: TBody): Promise<TResponse> {
    const response = await this.axiosInstance.patch(url, body);
    return response.data as TResponse;
  }

  public static async delete<T>(url: string): Promise<T> {
    const response = await this.axiosInstance.delete(url);
    return response.data as T;
  }

  public static async put<TResponse, TBody = any>(url: string, body?: TBody): Promise<TResponse> {
    const response = await this.axiosInstance.put(url, body);
    return response.data as TResponse;
  }
}

export default ApiV2;
